import { useMemo } from "react";
import type { ClientsWorkOverviewWorkspaceRow } from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import { useBusyClosesDemoTicker } from "./busyClosesDemoTicker";
import { buildDemoWorkDataset } from "./buildDemoWorkRows";
import {
    fromDemoKey,
    isDemoWorkRowShown,
    mergeDemoWorkRows,
    toDemoKey,
} from "./demoFilters";
import {
    useDemoClock,
    useIsBusyClosesDemoOn,
    useRegisterDemoDataset,
} from "./useBusyClosesDemoGate";
import { useDemoOrganizationSources, useDemoSelfId } from "./useDemoSources";

export { useBusyClosesDemoTasks } from "./useBusyClosesDemoTasks";

/** Org Home rows plus about 14 filtered fake applications; `workspaceRows` itself while off or nothing fake matches. */
export function useBusyClosesDemoWorkRows({
    workspaceRows,
    filters,
}: {
    workspaceRows: ClientsWorkOverviewWorkspaceRow[] | null;
    filters: {
        search: string;
        clientIds: readonly string[];
        memberUserIds: readonly number[];
    };
}): ClientsWorkOverviewWorkspaceRow[] | null {
    const isOn = useIsBusyClosesDemoOn();
    const sources = useDemoOrganizationSources({ isEnabled: isOn });
    const selfId = useDemoSelfId();
    const clock = useDemoClock();
    useBusyClosesDemoTicker(isOn);

    const dataset = useMemo(
        () =>
            isOn && sources.isReady
                ? buildDemoWorkDataset({
                      datasetKey: `work:${sources.organizationId ?? "none"}`,
                      clients: sources.clients,
                      people: sources.people,
                      selfId,
                      stages: sources.stages,
                      templates: sources.templates,
                      today: clock.today,
                      now: clock.now,
                  })
                : null,
        [isOn, sources, selfId, clock],
    );
    useRegisterDemoDataset(dataset);

    const { search } = filters;
    const clientIdsKey = toDemoKey(filters.clientIds);
    const memberUserIdsKey = toDemoKey(filters.memberUserIds);
    return useMemo(() => {
        if (workspaceRows === null || dataset === null) {
            return workspaceRows;
        }
        const demoFilters = {
            search,
            clientIds: fromDemoKey(clientIdsKey),
            memberUserIds: fromDemoKey(memberUserIdsKey).map(Number),
        };
        return mergeDemoWorkRows({
            workspaceRows,
            demoRows: dataset.rows.filter((row) =>
                isDemoWorkRowShown({ row, filters: demoFilters }),
            ),
        });
    }, [workspaceRows, dataset, search, clientIdsKey, memberUserIdsKey]);
}
